/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'

import colors from '@sportninja/common/constants/appColors'
import type {
  GameLike,
  VenueRef,
} from '@sportninja/common/features/game/gameListView/types'
import type { SnEvent } from '@sportninja/common/features/event/types'

import {
  CalendarAgendaView,
  CalendarDisplayProvider,
  CalendarGrid,
  CalendarWeekGrid,
  DayTimeGrid,
} from 'src/components/List/GameList/CalendarView'
import { font } from 'src/components/css'
import type { CalendarSubView } from '../hooks/useGameViewMode'
import type { GameListCounts } from '../hooks/useGameListItems'
import { gameViewsLabel } from '../utils/i18n'
import LoadMoreFooter from './LoadMoreFooter'

const SUB_VIEWS: CalendarSubView[] = ['month', 'week', 'day', 'agenda']

const SUB_VIEW_LABEL: Record<CalendarSubView, string> = {
  month: 'calendarSubViewMonth',
  week: 'calendarSubViewWeek',
  day: 'calendarSubViewDay',
  agenda: 'calendarSubViewAgenda',
}

export interface CalendarPanelProps {
  games: GameLike[]
  /**
   * Shown alongside the games in every sub-view, marked off by a distinct
   * border colour on the card. Already date-filtered by the host — the
   * calendar does not filter, it only lays out what it is given.
   */
  events?: SnEvent[]
  venuesById: Map<string, VenueRef>
  subView: CalendarSubView
  onSubViewChange: (next: CalendarSubView) => void
  anchorDate: string | null
  onAnchorDateChange: (next: string) => void
  /**
   * Optional. The Schedule views derive the query window from the URL anchor
   * instead, so nothing needs reporting up; this stays for hosts that let the
   * calendar own its own paging.
   */
  onRangeChange?: (start: string, endInclusive: string) => void
  isSoccer: boolean
  isHockey: boolean
  loading: boolean
  gameStatusTypes: unknown[]
  gameTypes: unknown[]
  /** Mobile forces agenda; the sub-view tabs would be dead controls there. */
  showSubViewTabs: boolean
  /**
   * Name each game's competition on its card. True when no single season is
   * filtered to, because then one cell can hold games from several of them and
   * time + teams + rink cannot tell a regular-season game from a playoff.
   */
  showSeasonName?: boolean
  counts: GameListCounts
  isError?: boolean
  errorMessage?: string
  onRetry?: () => void
  isLoadingAll?: boolean
  onLoadAll?: () => void
}

/**
 * Hosts the four calendar layouts over the SAME dataset the List and Details
 * views read. Nothing here refetches on its own — changing month/week/day tells
 * the parent which window it wants and the shared query does the rest, so the
 * three views can never diverge on what "November" contains.
 */
const CalendarPanel = ({
  games,
  events,
  venuesById,
  subView,
  onSubViewChange,
  anchorDate,
  onAnchorDateChange,
  onRangeChange,
  isSoccer,
  isHockey,
  loading,
  gameStatusTypes,
  gameTypes,
  showSubViewTabs,
  showSeasonName = false,
  counts,
  isError,
  errorMessage,
  onRetry,
  isLoadingAll,
  onLoadAll,
}: CalendarPanelProps) => {
  'use memo'

  const items = games as never[]

  return (
    <div css={containerStyles}>
      {showSubViewTabs && (
        <div
          css={tabsStyles}
          role='group'
          aria-label={gameViewsLabel('calendarSubViewLabel')}
        >
          {SUB_VIEWS.map((value, index) => (
            <button
              key={value}
              type='button'
              aria-pressed={subView === value}
              data-testid={`calendar-subview-${value}`}
              onClick={() => onSubViewChange(value)}
              css={tabStyles({
                isActive: subView === value,
                isFirst: index === 0,
                isLast: index === SUB_VIEWS.length - 1,
              })}
            >
              {gameViewsLabel(SUB_VIEW_LABEL[value])}
            </button>
          ))}
        </div>
      )}

      <CalendarDisplayProvider showSeasonName={showSeasonName}>
        {subView === 'week' ? (
          <CalendarWeekGrid
            items={items}
            events={events}
            isSoccer={isSoccer}
            isHockey={isHockey}
            loading={loading}
            anchorDate={anchorDate}
            onAnchorDateChange={onAnchorDateChange}
            onWeekChange={onRangeChange}
          />
        ) : subView === 'day' ? (
          <DayTimeGrid
            items={items}
            events={events}
            isSoccer={isSoccer}
            isHockey={isHockey}
            loading={loading}
            venuesById={venuesById}
            anchorDate={anchorDate}
            onAnchorDateChange={onAnchorDateChange}
            onDayChange={onRangeChange}
          />
        ) : subView === 'agenda' ? (
          <CalendarAgendaView
            items={items}
            events={events}
            isSoccer={isSoccer}
            isHockey={isHockey}
            loading={loading}
          />
        ) : (
          <CalendarGrid
            items={items}
            events={events}
            gameStatusTypes={gameStatusTypes as never[]}
            gameTypes={gameTypes as never[]}
            isSoccer={isSoccer}
            isHockey={isHockey}
            loading={loading}
            anchorDate={anchorDate}
            onAnchorDateChange={onAnchorDateChange}
            onMonthChange={onRangeChange}
          />
        )}
      </CalendarDisplayProvider>

      {/*
        A bounded layout draws its window all at once and has nothing to scroll,
        so a partially loaded month is simply wrong — and a failed page leaves it
        looking complete. Both have to be visible here.

        Agenda scrolls and pages as it goes, so for it this footer reports honest
        progress and offers "Load all" to anyone who wants the whole range.
      */}
      <LoadMoreFooter
        counts={counts}
        isFetchingNextPage={false}
        isLoading={loading}
        visibleCount={games.length}
        showIncomplete
        isError={isError}
        errorMessage={errorMessage}
        onRetry={onRetry}
        isLoadingAll={isLoadingAll}
        onLoadAll={onLoadAll}
      />
    </div>
  )
}

// ─── Styles ──────────────────────────────────────────────────────

const containerStyles = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
`

const tabsStyles = css`
  display: flex;
  align-items: center;
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 8px;
  height: 40px;
  width: fit-content;
  margin-bottom: 12px;
`

const tabStyles = ({
  isActive,
  isFirst,
  isLast,
}: {
  isActive: boolean
  isFirst: boolean
  isLast: boolean
}) => css`
  ${font.body}
  display: flex;
  align-items: center;
  justify-content: center;
  width: 88px;
  height: 40px;
  border-radius: ${isFirst ? '8px 0 0 8px' : isLast ? '0 8px 8px 0' : '0'};
  background-color: ${isActive ? colors.SECONDARY_300 : 'transparent'};
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  font-weight: ${isActive ? 700 : 400};
  color: ${colors.NEUTRAL_0};

  &:hover {
    background-color: ${isActive ? colors.SECONDARY_300 : colors.SECONDARY_200};
  }
  &:focus-visible {
    outline: 2px solid ${colors.PRIMARY};
    outline-offset: -2px;
  }
`

export default CalendarPanel
